<template>
    <kiosk-team-details :teamId="props.teamId">
        <div class="flex flex-col w-full max-w-(--breakpoint-lg) justify-center">
            <page-header class="mb-2 md:mb-2">
                <template v-slot:subtitle>
                    <a
                        :href="`/kiosk/teams/${props.teamId}/phone-numbers`"
                        class="no-underline! text-sky-600! text-base font-normal"
                    >
                        <font-awesome-icon widthAuto class="mr-1" :icon="faArrowLeft"></font-awesome-icon>
                        All Phone Numbers
                    </a>
                </template>
                Voice Changes: {{ formatPhoneNumber(props.phoneNumber) }}
            </page-header>

            <page-controls>
                <template v-slot:left>
                    <vx-button
                        @click.prevent="toggleSort()"
                        type="button"
                        color="white"
                        size="lg"
                        shadow
                        data-test="sort-toggle"
                    >
                        <font-awesome-icon
                            widthAuto
                            :icon="fetchParams.sort_dir === 'asc' ? faArrowUpWideShort : faArrowDownWideShort"
                        ></font-awesome-icon>
                        <span class="ms-2">{{ fetchParams.sort_dir === 'asc' ? 'Oldest first' : 'Newest first' }}</span>
                    </vx-button>
                </template>
            </page-controls>

            <div v-if="loading && !list.length" class="text-center py-10">
                <vx-icon-loading class="w-9 h-9 animate-loading-spin"></vx-icon-loading>
            </div>

            <template v-if="!loading && !list.length">
                <empty-state>No voice routing changes have been recorded for this number.</empty-state>
            </template>

            <vx-table v-if="list.length" class="lg:grid-cols-5" rowBreakpoint="lg">
                <vx-table-row isHeader>
                    <div>Changed at</div>
                    <div>Field</div>
                    <div>Change</div>
                    <div>Changed by</div>
                    <div>Requested by</div>
                </vx-table-row>

                <kiosk-team-phone-number-voice-change-row v-for="entry in list" :key="entry.id" :modelValue="entry" />
            </vx-table>

            <div v-if="fetchParams.cursor" class="mt-8">
                <vx-button
                    @click="loadData()"
                    color="muted-dark"
                    size="xl"
                    class="grow-0"
                    data-test="load-more-btn"
                    :loading="loading"
                >
                    Load More
                </vx-button>
            </div>
        </div>
    </kiosk-team-details>
</template>

<script setup lang="ts">
import { onMounted } from 'vue';
import { VxButton, VxTable, VxTableRow, VxIconLoading } from '@voxie/frontend-components';
import { faArrowDownWideShort, faArrowLeft, faArrowUpWideShort } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import KioskTeamDetails from '~/components/kiosk/kiosk-teams/details/KioskTeamDetails.vue';
import PageHeader from '~/components/general/PageHeader.vue';
import PageControls from '~/components/general/PageControls.vue';
import EmptyState from '~/components/general/EmptyState.vue';
import KioskTeamPhoneNumberVoiceChangeRow, {
    type PhoneNumberVoiceChangeEntry,
} from './KioskTeamPhoneNumberVoiceChangeRow.vue';
import { useCursorPagination } from '~/composables/useCursorPagination';
import { adminPhoneNumbersClient } from '~/services';
import { formatPhoneNumber } from '~/components/filters';

interface VoiceChangesParams {
    sort_dir: 'asc' | 'desc';
    cursor: string | null;
    per_page: number;
}

const props = defineProps<{
    teamId: number;
    phoneNumberId: number;
    phoneNumber: string;
}>();

const { list, loading, fetchParams, fetchRecords } = useCursorPagination<
    PhoneNumberVoiceChangeEntry,
    VoiceChangesParams
>();

const listCancelKey = Symbol('kiosk-team-phone-number-voice-changes');

const loadData = () => {
    fetchRecords(
        adminPhoneNumbersClient.getVoiceChanges(
            props.teamId,
            props.phoneNumberId,
            { ...fetchParams.value },
            { cancelKey: listCancelKey }
        )
    );
};

const toggleSort = () => {
    fetchParams.value = {
        ...fetchParams.value,
        sort_dir: fetchParams.value.sort_dir === 'asc' ? 'desc' : 'asc',
        cursor: null,
    };
    loadData();
};

onMounted(() => {
    fetchParams.value = { sort_dir: 'desc', cursor: null, per_page: 20 };
    loadData();
});
</script>
